import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import DemoBanner from './DemoBanner';
import TrialBanner from './TrialBanner';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';

/**
 * AdminProtectedRoute - Wrapper component that protects admin routes
 * Checks for valid admin token before rendering children
 * Redirects to /admin/login if not authenticated
 * Redirects to /admin/suspended if account is suspended
 *
 * The tenant object from /api/admin/validate is held in state and passed to
 * TrialBanner. It now carries the tier, the trial end date and the days
 * remaining, so a tenant whose trial has lapsed but who has not yet been
 * suspended by the nightly cron sees a banner explaining why their writes are
 * being refused, instead of a normal dashboard that 403s on every click.
 */
function AdminProtectedRoute({ children, allowSuspended = false }) {
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const location = useLocation();
  const { adoptRemoteTheme } = useTheme();

  useEffect(() => {
    const validateAdminToken = async () => {
      const token = localStorage.getItem('adminToken');

      if (!token) {
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }

      try {
        // Validate token by making a request to an admin-only endpoint
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/validate`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(true);
          setTenant(data.tenant || null);
          // Check if tenant is suspended
          setIsSuspended(data.tenant?.isSuspended === true);

          // The admin's own stored theme, which is authoritative over this
          // browser's cached copy: a choice made on one machine should show up
          // on the next. Absent means they never picked one, and the local
          // value or the device preference stands.
          if (data.admin?.themePreference) {
            adoptRemoteTheme(data.admin.themePreference);
          }
        } else if (response.status === 503) {
          // The server could not establish the account's standing. That gate
          // now fails closed rather than assuming the account is fine, so the
          // correct client response is to say so and stop, NOT to discard a
          // token that may be perfectly valid. Logging the admin out on a
          // transient database fault would turn a blip into a support call.
          setServiceUnavailable(true);
          setIsAuthenticated(false);
        } else {
          // Token is invalid or expired - clear it
          localStorage.removeItem('adminToken');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Admin token validation error:', error);
        // Network error, the request never reached the server. Fall back to the
        // locally decoded token so the admin is not logged out by a dropped
        // connection. Suspension cannot be checked from here, and this is
        // advisory only: every gate that matters is enforced server side, so a
        // suspended tenant reaching the dashboard this way still cannot read or
        // write anything the server will serve them.
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const isExpired = tokenPayload.exp * 1000 < Date.now();
          const isAdmin = tokenPayload.role === 'admin';

          if (!isExpired && isAdmin) {
            setIsAuthenticated(true);
            setIsSuspended(false);
            setTenant(null);
          } else {
            localStorage.removeItem('adminToken');
            setIsAuthenticated(false);
          }
        } catch {
          localStorage.removeItem('adminToken');
          setIsAuthenticated(false);
        }
      }

      setIsValidating(false);
    };

    validateAdminToken();
    // adoptRemoteTheme is a stable useCallback from ThemeProvider. It is listed
    // because CRA runs the build with CI=true, which promotes the
    // exhaustive-deps warning to a build failure.
  }, [adoptRemoteTheme]);

  // Show loading state while validating
  if (isValidating) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--color-background)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--color-border)',
            borderTop: '4px solid var(--color-brand-text)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--color-text-light)' }}>Validating admin access...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // The server refused to confirm the account's standing. Say so plainly and
  // keep the token, rather than bouncing to the login page where signing in
  // again would hit the same fault.
  if (serviceUnavailable) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--color-background)',
        padding: '24px'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '520px' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: '20px', color: 'var(--color-error-text)' }}>
            We cannot reach your account right now
          </h2>
          <p style={{ color: 'var(--color-text-light)', lineHeight: 1.6 }}>
            Your account status could not be confirmed, so access is paused for safety.
            This is usually temporary. Please try again in a moment.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '18px',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              background: 'var(--color-primary)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '15px'
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Redirect to admin login if not authenticated
  if (!isAuthenticated) {
    // Pass the current location so we can redirect back after login
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Redirect to suspension page if suspended (unless this route allows suspended access)
  if (isSuspended && !allowSuspended) {
    return <Navigate to="/admin/suspended" replace />;
  }

  // Render children if authenticated (and not suspended, or suspension allowed)
  return (
    <>
      <DemoBanner />
      <TrialBanner tenant={tenant} />
      {children}

      {/* The tenant admin app has no shared header component: every admin page
          renders its own title block, so there is no one header to put this in.
          This wrapper is the only thing every admin route passes through, so the
          control is rendered here as a floating affordance. Doing it per page
          would mean editing roughly thirty files and would still miss the next
          page someone adds. */}
      <ThemeToggle className="theme-toggle-floating" />
    </>
  );
}

export default AdminProtectedRoute;
